import axios from "axios";
import { REACT_APP_API_END_POINT, REACT_APP_PORTAL_URL } from "src/constant/environment";
import { ACCESS_TOKEN, getLocalStorage, removeLocalStorage } from "src/utils/storage";
import { STATUS_ERROR_CODE } from "src/constant/common";
import { setRecoil } from "recoil-nexus";
import { alertState } from "src/recoil/atoms/global";
import APP_ROUTES from "src/routes/routes";

const defaultOptions = {
  withCredentials: false,
};

const getProjectIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("projectId");
};

const getBasicAuthen = () => {
  if (getLocalStorage(ACCESS_TOKEN)) {
    return {
      Authorization: `Bearer ${getLocalStorage(ACCESS_TOKEN)}`,
    };
  }

  return {};
};

const headerDefault = () => {
  const projectId = getProjectIdFromUrl();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(projectId && { "X-Project-Id": projectId }),
    ...getBasicAuthen(),
  };
};

const API_URL = REACT_APP_API_END_POINT;

function getApi(path, option = {}, apiURL) {
  const response = axios.get(`${apiURL || API_URL}/${path.replace(/^\//, "")}`, {
    ...defaultOptions,
    ...option,
    headers: {
      ...headerDefault(),
      ...option?.headers,
    },
  });

  return response;
}

function postApi(path, option = {}) {
  const response = axios.post(`${API_URL}/${path.replace(/^\//, "")}`, option?.data, {
    ...defaultOptions,
    ...option,
    headers: {
      ...headerDefault(),
      ...option?.headers,
    },
  });

  return response;
}

function putApi(path, option = {}) {
  const response = axios.put(`${API_URL}/${path.replace(/^\//, "")}`, option?.data, {
    ...defaultOptions,
    ...option,
    headers: {
      ...headerDefault(),
      ...option.headers,
    },
  });

  return response;
}

function patchApi(path, option = {}) {
  const response = axios.patch(`${API_URL}/${path.replace(/^\//, "")}`, option?.data, {
    ...defaultOptions,
    ...option,
    headers: {
      ...headerDefault(),
      ...option.headers,
    },
  });

  return response;
}

function deleteApi(path, option = {}) {
  const response = axios.delete(`${API_URL}/${path.replace(/^\//, "")}`, {
    ...defaultOptions,
    ...option,
    headers: {
      ...headerDefault(),
      ...option.headers,
    },
  });

  return response;
}

const handleErrors = (serverResponse) => {
  if (serverResponse.status === STATUS_ERROR_CODE.NOT_AUTHORIZED) {
    removeLocalStorage(ACCESS_TOKEN);
    window.location.href = REACT_APP_PORTAL_URL;
    return serverResponse;
  }
  if (serverResponse.status === STATUS_ERROR_CODE.FORBIDDEN) {
    window.location.href = APP_ROUTES.ACCESS_DENIED;
    return serverResponse;
  }

  if (serverResponse.status === 500) {
    setRecoil(alertState, () => ({
      status: true,
      message: "An unknown error occurred. If the problem continues, please contact us.",
      type: "danger",
    }));
    return serverResponse;
  }

  if (serverResponse.status === 503) {
    setRecoil(alertState, () => ({
      status: true,
      message: "The server is temporarily not responsive to status check. Please try again later",
      type: "danger",
    }));
    return serverResponse;
  }
  return null;
};

axios.interceptors.response.use(
  (response) => {
    if (handleErrors(response)) return;
    return response;
  },
  (error) => {
    if (typeof error.response == "undefined") {
      setRecoil(alertState, () => ({
        status: true,
        message: "No Internet. Please check your connection and try again.",
        type: "danger",
      }));
      return Promise.reject(new Error("No Internet"));
    }
    handleErrors(error.response);
    return Promise.reject(error);
  }
);

const Api = {
  get: getApi,
  post: postApi,
  put: putApi,
  patch: patchApi,
  delete: deleteApi,
};

export default Api;
